import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  CheckboxMultipleSelectionEvent,
  LoadingIndicatorComponent,
  ModalComponent,
  NotificationComponent,
  SortDirection,
  TableHeaderColumnComponent
} from '@epsilon/core-ui';
import { AudienceAIChatService } from '../audience-ai-chat.service';
import { PickListResponse, PickListValues } from '../audience-ai.model';
import { auditTime, finalize, pairwise, startWith, Subject } from 'rxjs';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'lib-picklist-modal',
  templateUrl: './picklist-modal.component.html',
  styleUrl: './picklist-modal.component.sass',
  standalone: false
})
export class PicklistModalComponent implements OnInit, OnDestroy {
  @Output() pickListValues = new EventEmitter<string>();

  @ViewChild('pickListModal')
  public pickListModal: ModalComponent;

  @ViewChild('pickListError', { static: true })
  public pickListError: NotificationComponent;

  @ViewChild(LoadingIndicatorComponent, { static: true })
  public loadingIndicator: LoadingIndicatorComponent;

  public showLoader = true;

  pickListResponse: PickListResponse;
  filteredPickListValues: PickListValues[] = [];
  isPickListValueSelected = false;
  headerCheckboxIndeterminate: boolean;

  pickListValueMap: Map<string, PickListResponse> = new Map();

  sortOption = 'id';
  sortDirection: SortDirection = 'ASC';

  ngUnsubscribe$ = new Subject<void>();

  pickListFormGroup = new FormGroup({
    search: new FormControl(),
    headerCheckbox: new FormControl(false),
    rows: new FormArray([])
  });

  public get rowsControl(): FormArray {
    return this.pickListFormGroup.get('rows') as FormArray;
  }

  constructor(public audienceAIChatService: AudienceAIChatService) {
    this.pickListFormGroup.valueChanges
      .pipe(
        startWith(() => this.pickListFormGroup.value),
        pairwise(),
        filter(([val1, val2]: [any, any]): boolean => {
          return val1.rows !== val2.rows && val2.rows && val2.rows.length > 0;
        }),
        map(([, val2]: [any, any]): boolean[] => val2.rows),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((rows: boolean[]) => {
        const everyTrue = rows.every((i) => i) && rows.length > 0;
        this.pickListFormGroup
          .get('headerCheckbox')
          .setValue(everyTrue, { emitEvent: false });
        this.headerCheckboxIndeterminate = !everyTrue && rows.some((i) => i);
        this.isPickListValueSelected = rows.some((i) => i);
      });

    this.pickListFormGroup
      .get('headerCheckbox')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value: boolean) => {
        const rowsValue = this.rowsControl.controls.map(() => value);
        this.rowsControl.setValue(rowsValue);
      });
  }

  ngOnInit(): void {
    this.lookForSearchChange();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  public launchModal(
    contextId: string,
    picklistId: string,
    attributeName: string
  ): void {
    this.resetValues();
    this.pickListModal.show();
    if (!this.pickListValueMap.has(picklistId)) {
      this.loadingIndicator.show();
      this.audienceAIChatService
        .getPicklistValues(contextId, picklistId)
        .pipe(
          finalize(() => {
            this.loadingIndicator.hide();
            this.showLoader = false;
          })
        )
        .subscribe({
          next: (response: PickListResponse) => {
            this.pickListValueMap.set(picklistId, response);
            this.setValues(response);
          },
          error: (error) => {
            if (error.status === 404) {
              this.pickListResponse = { name: attributeName, values: [] };
            } else {
              console.error('Error fetching the pickList Values', error.status);
              this.pickListModal.hide().then(() => {
                this.pickListError.show();
              });
            }
          }
        });
    } else {
      this.setValues(this.pickListValueMap.get(picklistId));
      this.showLoader = false;
    }
  }

  private lookForSearchChange(): void {
    this.pickListFormGroup
      .get('search')
      .valueChanges.pipe(auditTime(250), startWith(''), pairwise())
      .subscribe(([prevSearchText, searchText]) => {
        if (prevSearchText === searchText) {
          return;
        }
        if (!searchText) {
          this.filteredPickListValues = this.pickListResponse?.values || [];
          return;
        }
        this.filteredPickListValues =
          this.pickListResponse?.values.filter((value) => {
            if (value.id && searchText) {
              return value.id.toLowerCase().includes(searchText.toLowerCase());
            }
            return false;
          }) || [];
      });
  }

  public sort(component: TableHeaderColumnComponent, option: string): void {
    this.sortOption = option;
    this.sortDirection = component.sortDirection;
  }

  public onMultipleSelected(event: CheckboxMultipleSelectionEvent): void {
    this.rowsControl.controls.forEach((control: FormControl, i: number) => {
      if (i >= event.indexes[0] && i <= event.indexes[1]) {
        control.setValue(event.checked);
      }
    });
  }

  public addToChat(): void {
    const filteredPickList =
      this.sortDirection === 'ASC'
        ? this.filteredPickListValues
        : this.filteredPickListValues.reverse();
    const selectedValues = filteredPickList
      .filter((_, index) => this.rowsControl.controls[index].value)
      .map((value) => value.id);
    this.pickListValues.emit(
      this.pickListResponse.name + ' = ' + selectedValues.join(', ')
    );
    this.pickListModal.hide();
  }

  private setValues(response: PickListResponse): void {
    this.pickListResponse = response;
    this.filteredPickListValues = response.values;
    this.setRowControls();
  }

  private setRowControls(): void {
    const rowArray = new FormArray(
      this.filteredPickListValues.map(() => new FormControl(false))
    );
    this.pickListFormGroup.setControl('rows', rowArray);
  }

  private resetValues(): void {
    this.pickListFormGroup.reset();
    this.rowsControl.clear();
    this.filteredPickListValues = [];
    this.pickListResponse = null;
    this.showLoader = true;
    this.isPickListValueSelected = false;
    this.sortDirection = 'ASC';
  }
}
